import { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Trans, useLingui } from '@lingui/react/macro';
import { accountService } from '../services/accountService';
import type { StripeTiersConfig, UpgradePromo } from '../services/accountService';
import { maxOfferService, offerPricing, promoDuration } from '../services/maxOfferService';
import type { MaxOfferTrial } from '../services/maxOfferService';
import { useMaxOffer } from '../lib/useMaxOffer';
import { loadCodes } from '../lib/arrival-codes';
import { sanitizeTypedCode, formatCents } from '../lib/codes';
import { trialCallUrl } from '../constants/maxTrial';
import { useTier } from '../contexts/TierContext';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import MaxBadge from './MaxBadge';

/**
 * The one Max checkout for every upsell surface (EPIC-15): paywalls, the
 * locked usage ranges, share-with-teammate, the subscription page, the signup
 * wizard's plan step, and the desktop app's deep link.
 *
 * Two ways in, not one. A self-serve buyer puts a card in and is charged
 * today. A company evaluating Max for a team does not want to put a card in
 * at all, and used to have nowhere to go: that buyer now enters a trial code
 * from sales, or books a call to get one.
 *
 *   TRIAL CODE KINDS, and the one fact this component branches on:
 *     stripe      requiresCard TRUE. The code is a Stripe-backed discount; a
 *                 card is still collected and Stripe runs the subscription.
 *     enterprise  requiresCard FALSE. No card at all. Max company-wide for
 *                 trialDays, then it drops back to Pro.
 *
 * An enterprise code is REDEEMED AT REGISTRATION (POST /api/user/register
 * carries trialCode), which is how the Max landing page activates one with no
 * card. privacypal-api exposes no authenticated redeem route, so for an
 * account that already exists this component previews the code, says exactly
 * what it grants, and hands the customer to sales with the code attached.
 * Previewing a code we cannot redeem here is still the right thing to show:
 * the customer learns their code is real and what it is worth before they
 * spend fifteen minutes on a call.
 *
 * Prices are NEVER computed here. GET /api/billing/public/max-offer prices the
 * promo code against Stripe and this renders the answer.
 *
 * Legacy path: an account that still has a live subscription (a pre-PLG
 * paying Pro) upgrades via the tier switch instead, no new card, prorated and
 * charged immediately.
 */

type PlanKey = 'annual' | 'monthly';
type MaxPath = 'card' | 'trial';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Where the upsell came from, e.g. 'connections-paywall'. Threaded into the
   * trial-call link so sales can see which surface produced the request, and
   * used to open the trial path straight away when the caller already knows
   * this is a company-wide conversation.
   */
  source?: string;
  /** Called after a successful upgrade, once the tier context is refreshed. */
  onUpgraded?: () => void;
  /** Promo code the visitor arrived with. Defaults to the stored arrival code. */
  initialPromoCode?: string;
  /** Trial code the visitor arrived with. Defaults to the stored arrival code. */
  initialTrialCode?: string;
  /** Which path opens first. Defaults to the trial path when a trial code is in hand. */
  initialPath?: MaxPath;
}

const panelStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface-secondary)',
  border: '1px solid var(--color-border)',
};

/* ------------------------------------------------------------------ *
 * Path 1: card. Charged today, no trial.
 * ------------------------------------------------------------------ */

function CheckoutForm({
  tiers,
  promo,
  initialPromoCode,
  attachedTrialCode,
  onUpgraded,
  onClose,
}: {
  tiers: StripeTiersConfig;
  promo: UpgradePromo;
  initialPromoCode: string;
  /**
   * A stripe-kind trial code the customer came in with. Shown so they can see
   * it is on the order, never sent as the promo code unless the public offer
   * endpoint confirmed that same string also resolves as a Stripe coupon.
   */
  attachedTrialCode: string;
  onUpgraded?: () => void;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { isDark } = useTheme();
  const { t } = useLingui();
  const { showToast } = useToast();
  const { refresh } = useTier();
  const [plan, setPlan] = useState<PlanKey>('monthly');
  // The arrival code wins over the standing offer: a customer who followed a
  // campaign link was promised that code's price, not the house default.
  const [promoCode, setPromoCode] = useState(initialPromoCode || promo?.code || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submittingRef = useRef(false);

  const slot = plan === 'annual' ? tiers?.max?.annual : tiers?.max?.monthly;
  const monthlySlot = tiers?.max?.monthly;
  const annualSlot = tiers?.max?.annual;

  // Stripe prices the code, not this component. One seat, because that is
  // what the subscription is created with; seats are added afterwards.
  const { offer } = useMaxOffer({ promoCode: promoCode.trim(), plan, seats: 1 });
  const pricing = offerPricing(offer);
  const appliedPromo = offer?.promo && offer.promo.valid ? offer.promo : null;
  const rejectedPromo = offer?.promo && !offer.promo.valid ? offer.promo : null;
  const duration = promoDuration(offer?.promo ?? null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError('');
    setLoading(true);
    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error(t`Card element not found`);
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      if (pmError || !paymentMethod) throw new Error(pmError?.message || t`Failed to create payment method`);

      const result = await accountService.createSubscription(
        paymentMethod.id,
        promoCode.trim() || undefined,
        plan,
        'max'
      );
      if (!result?.success) throw new Error(result?.error || t`Failed to complete the upgrade`);

      await refresh();
      showToast(t`Welcome to PrivacyPal Max. Every surface is now protected.`, 'success');
      onUpgraded?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || t`Payment failed`);
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const cardStyle = {
    base: {
      fontSize: '16px',
      color: isDark ? '#e2e8f0' : '#0f172a',
      fontFamily: "'Inter', sans-serif",
      '::placeholder': { color: isDark ? '#64748b' : '#94a3b8' },
    },
    invalid: { color: '#ef4444' },
  };

  // What is actually charged today, from the server's own total.
  const chargedCents = appliedPromo?.totalCents ?? offer?.totalCents ?? null;

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Billing interval */}
      <div className="grid grid-cols-2 gap-2">
        {(['monthly', 'annual'] as PlanKey[]).map((key) => {
          const optionSlot = key === 'annual' ? annualSlot : monthlySlot;
          const selected = plan === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setPlan(key)}
              className="p-3 rounded-lg text-left transition-all"
              style={{
                border: selected ? '2px solid var(--color-max-accent)' : '1px solid var(--color-border)',
                backgroundColor: selected ? 'var(--color-max-bg)' : 'var(--color-surface)',
              }}
            >
              <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                {key === 'annual' ? <Trans>Annual</Trans> : <Trans>Monthly</Trans>}
              </div>
              <div className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
                {optionSlot?.formatted || '—'}
              </div>
              {key === 'annual' && optionSlot?.perSeatAmount != null && (
                <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  <Trans>{optionSlot.perSeatAmount}/seat/mo billed yearly</Trans>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Promo */}
      <div>
        <label className="block text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
          <Trans>Promo Code</Trans>
        </label>
        <input
          type="text"
          value={promoCode}
          onChange={(e) => setPromoCode(sanitizeTypedCode(e.target.value))}
          className="w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
        />
        {appliedPromo && pricing.discounted && (
          <p className="mt-1.5 text-sm font-medium" style={{ color: 'var(--color-max-text)' }}>
            {duration.kind === 'repeating' ? (
              <Trans>{appliedPromo.code} applied: {pricing.perSeatLabel}/seat/mo for your first {duration.months} months</Trans>
            ) : duration.kind === 'once' ? (
              <Trans>{appliedPromo.code} applied: {pricing.perSeatLabel}/seat for your first month</Trans>
            ) : duration.kind === 'forever' ? (
              <Trans>{appliedPromo.code} applied: {pricing.perSeatLabel}/seat/mo for as long as you stay</Trans>
            ) : (
              <Trans>{appliedPromo.code} applied: {pricing.perSeatLabel}/seat/mo</Trans>
            )}
          </p>
        )}
        {/* Only once the answer is about the code CURRENTLY in the field.
            The offer is debounced, so without this a customer typing a long
            code is told it is wrong at every keystroke along the way. */}
        {rejectedPromo && rejectedPromo.code === promoCode.trim().toUpperCase() && (
          <p className="mt-1.5 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {rejectedPromo.message || <Trans>We could not apply that code, so this is the list price.</Trans>}
          </p>
        )}
      </div>

      {attachedTrialCode && (
        <div className="p-3 rounded-lg text-sm" style={panelStyle}>
          <Trans>
            Trial code <span className="font-semibold">{attachedTrialCode}</span> needs a card, so it runs as a normal Max subscription.
          </Trans>
        </div>
      )}

      {/* Card */}
      <div>
        <label className="block text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
          <Trans>Card Details</Trans>
        </label>
        <div className="p-4 rounded-lg" style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
          <CardElement options={{ style: cardStyle }} />
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-risk-bg)', border: '1px solid var(--color-risk-text)', color: 'var(--color-risk-text)' }} role="alert">
          {error}
        </div>
      )}

      {/* Order summary: the charge is immediate, say so plainly. */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-max-border)' }}>
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2">
            PrivacyPal Max <MaxBadge />
          </div>
          <div className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>
            {pricing.discounted && chargedCents != null ? (
              <>
                <span className="line-through mr-2 text-sm font-normal" style={{ color: 'var(--color-text-muted)' }}>
                  {slot?.formatted || ''}
                </span>
                {formatCents(chargedCents)}
              </>
            ) : (
              slot?.formatted || '—'
            )}
          </div>
        </div>
        <p className="text-xs mt-2 mb-0" style={{ color: 'var(--color-text-muted)' }}>
          <Trans>Charged today. No trial period. Cancel anytime; you keep the free plan.</Trans>
        </p>
      </div>

      <button
        type="submit"
        className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:shadow-lg disabled:opacity-50"
        style={{ backgroundColor: 'var(--color-max-accent)' }}
        disabled={loading || !stripe}
      >
        {loading ? t`Processing...` : t`Upgrade to Max now`}
      </button>
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * Path 2: a company-wide trial. No card unless the code says so.
 * ------------------------------------------------------------------ */

function TrialCodeForm({
  initialTrialCode,
  source,
  referralCode,
  onUseCard,
}: {
  initialTrialCode: string;
  source?: string;
  referralCode: string;
  /** Hand over to the card path. `promoCode` is set only when verified as a coupon. */
  onUseCard: (carry: { trialCode: string; promoCode: string }) => void;
}) {
  const { t } = useLingui();
  const [code, setCode] = useState(initialTrialCode);
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState<MaxOfferTrial>(null);
  // Set when the typed trial code ALSO resolves as a Stripe coupon, which is
  // the only circumstance in which it is safe to put into the promo field.
  const [codeIsAlsoCoupon, setCodeIsAlsoCoupon] = useState(false);

  const checkCode = async (raw: string) => {
    const typed = raw.trim();
    if (!typed) return;
    setChecking(true);
    setCodeIsAlsoCoupon(false);
    try {
      // One call answers both questions: what the trial grants, and whether
      // this same string is a Stripe coupon. Asking rather than assuming is
      // the point: a stripe-kind trial code carries a promotion code that is
      // not necessarily spelled the same as the code sales handed out.
      const offer = await maxOfferService.getOffer({ trialCode: typed, promoCode: typed, seats: 1 });
      setPreview(offer ? offer.trial : { valid: false, code: typed, reason: 'UNAVAILABLE', message: null });
      setCodeIsAlsoCoupon(Boolean(offer?.promo && offer.promo.valid));
    } finally {
      setChecking(false);
    }
  };

  // A code that arrived on the URL is checked without being asked for: the
  // customer already handed it over by following the link. Once only, even if
  // the modal re-renders, so one arrival is one call against a rate-limited
  // endpoint.
  const autoCheckedRef = useRef(false);
  useEffect(() => {
    if (autoCheckedRef.current || !initialTrialCode) return;
    autoCheckedRef.current = true;
    void checkCode(initialTrialCode);
  }, [initialTrialCode]);

  const valid = preview && preview.valid ? preview : null;
  const invalid = preview && !preview.valid ? preview : null;
  const bookingUrl = trialCallUrl({ source, referralCode, trialCode: valid?.code || code.trim() || undefined });

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
          <Trans>Trial Code</Trans>
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => { setCode(sanitizeTypedCode(e.target.value)); setPreview(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void checkCode(code); } }}
            placeholder={t`MAX-XXXX-XXXX`}
            className="flex-1 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
          />
          <button
            type="button"
            onClick={() => void checkCode(code)}
            disabled={checking || !code.trim()}
            className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={{ border: '1px solid var(--color-max-border)', color: 'var(--color-max-text)', backgroundColor: 'var(--color-max-bg)' }}
          >
            {checking ? t`Checking...` : t`Check code`}
          </button>
        </div>
        {invalid && (
          <p className="mt-1.5 text-sm" style={{ color: 'var(--color-risk-text)' }}>
            {invalid.message || <Trans>We could not check that code just now. Try again in a moment.</Trans>}
          </p>
        )}
      </div>

      {valid && valid.requiresCard === false && (
        <div className="p-4 rounded-lg space-y-3" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-max-border)' }}>
          <div className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
            <Trans>No card needed</Trans> <MaxBadge />
          </div>
          <p className="text-sm mb-0" style={{ color: 'var(--color-text-secondary)' }}>
            {valid.maxSeats != null ? (
              <Trans>
                {valid.code} is good for {valid.trialDays} days of Max across up to {valid.maxSeats} seats. When it ends your company drops back to Pro, with nothing to cancel.
              </Trans>
            ) : (
              <Trans>
                {valid.code} is good for {valid.trialDays} days of Max company-wide. When it ends your company drops back to Pro, with nothing to cancel.
              </Trans>
            )}
          </p>
          {/* An enterprise code is redeemed at account creation. For a company
              that already has an account, our team switches it on. */}
          <p className="text-sm mb-0" style={{ color: 'var(--color-text-muted)' }}>
            <Trans>Your account already exists, so we activate this one for you. Book a 15 minute call and we will switch it on while you are on it.</Trans>
          </p>
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full py-3 rounded-lg font-semibold text-white text-center transition-all hover:shadow-lg"
            style={{ backgroundColor: 'var(--color-max-accent)' }}
          >
            <Trans>Activate {valid.code} on a call</Trans>
          </a>
        </div>
      )}

      {valid && valid.requiresCard !== false && (
        <div className="p-4 rounded-lg space-y-3" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-max-border)' }}>
          <div className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            <Trans>{valid.code} needs a card</Trans>
          </div>
          <p className="text-sm mb-0" style={{ color: 'var(--color-text-secondary)' }}>
            {valid.trialDays != null ? (
              <Trans>This code runs Max as a normal subscription for {valid.trialDays} days before it bills. Your card is collected now and nothing is charged until then.</Trans>
            ) : (
              <Trans>This code runs Max as a normal subscription, so your card is collected now.</Trans>
            )}
          </p>
          <button
            type="button"
            onClick={() => onUseCard({ trialCode: valid.code, promoCode: codeIsAlsoCoupon ? valid.code : '' })}
            className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:shadow-lg"
            style={{ backgroundColor: 'var(--color-max-accent)' }}
          >
            <Trans>Continue with a card</Trans>
          </button>
        </div>
      )}

      {!valid && (
        <div className="p-4 rounded-lg space-y-3" style={panelStyle}>
          <div className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            <Trans>No code yet?</Trans>
          </div>
          <p className="text-sm mb-0" style={{ color: 'var(--color-text-secondary)' }}>
            <Trans>Tell us how many people you need covered and we will issue a company-wide trial code on the call. No card, no auto-renewal.</Trans>
          </p>
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full py-3 rounded-lg font-semibold text-center transition-all hover:shadow-lg"
            style={{ border: '1px solid var(--color-max-border)', color: 'var(--color-max-text)', backgroundColor: 'var(--color-max-bg)' }}
          >
            <Trans>Book a trial call</Trans>
          </a>
        </div>
      )}
    </div>
  );
}

/** Legacy path: a live pre-PLG subscription upgrades in place, no new card. */
function TierSwitchForm({ onUpgraded, onClose }: { onUpgraded?: () => void; onClose: () => void }) {
  const { t } = useLingui();
  const { showToast } = useToast();
  const { refresh } = useTier();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const upgrade = async () => {
    setLoading(true);
    setError('');
    try {
      await accountService.updateSubscriptionTier('max');
      await refresh();
      showToast(t`Welcome to PrivacyPal Max. Every surface is now protected.`, 'success');
      onUpgraded?.();
      onClose();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'PAYMENT_REQUIRED') {
        setError(t`Your card was declined. Update your payment method on the Subscription page and try again.`);
      } else {
        setError(err?.response?.data?.error || err?.message || t`Failed to complete the upgrade`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
        <Trans>Your existing subscription moves to Max using the card on file. The difference is prorated and charged immediately.</Trans>
      </p>
      {error && (
        <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-risk-bg)', border: '1px solid var(--color-risk-text)', color: 'var(--color-risk-text)' }} role="alert">
          {error}
        </div>
      )}
      <button
        type="button"
        onClick={upgrade}
        className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:shadow-lg disabled:opacity-50"
        style={{ backgroundColor: 'var(--color-max-accent)' }}
        disabled={loading}
      >
        {loading ? t`Processing...` : t`Upgrade to Max now`}
      </button>
    </div>
  );
}

export default function UpgradeModal({
  open,
  onClose,
  onUpgraded,
  source,
  initialPromoCode,
  initialTrialCode,
  initialPath,
}: UpgradeModalProps) {
  const { account } = useTier();
  const [config, setConfig] = useState<Awaited<ReturnType<typeof accountService.getStripeConfig>> | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  // The codes the visitor arrived with. Props win so a caller can be explicit;
  // sessionStorage covers every other surface, including a modal opened days
  // into the signup session on a different page.
  const [stored] = useState(() => loadCodes());
  const promoCode = initialPromoCode ?? stored.promoCode;
  const trialCode = initialTrialCode ?? stored.trialCode;
  const referralCode = stored.referralCode;

  const [path, setPath] = useState<MaxPath>(initialPath ?? (trialCode ? 'trial' : 'card'));
  // Set when the trial path hands a stripe-kind code over to the card path.
  const [carried, setCarried] = useState<{ trialCode: string; promoCode: string } | null>(null);

  /**
   * The modal stays mounted when closed (the early return below is after the
   * hooks), so its state survives a close. Reset the chosen path every time
   * it opens, or a caller that opens it on the trial path once would be stuck
   * there on the next, unrelated upsell.
   */
  useEffect(() => {
    if (!open) return;
    setPath(initialPath ?? (trialCode ? 'trial' : 'card'));
    setCarried(null);
  }, [open, initialPath, trialCode]);

  useEffect(() => {
    if (!open || config) return;
    let cancelled = false;
    accountService.getStripeConfig().then((cfg) => {
      if (cancelled) return;
      setConfig(cfg);
      if (cfg.publishableKey) setStripePromise(loadStripe(cfg.publishableKey));
    });
    return () => {
      cancelled = true;
    };
  }, [open, config]);

  if (!open) return null;

  // A live legacy subscription upgrades in place; everything else (free Pro)
  // chooses between the card and a company-wide trial.
  const hasLiveSubscription = !!account?.subscriptionId &&
    ['active', 'trialing', 'past_due'].includes(account?.subscriptionStatus);

  const cardPanel = !config ? (
    <div className="loading py-8">
      <div className="spinner"></div>
    </div>
  ) : !config.publishableKey || !stripePromise ? (
    <p className="text-sm" style={{ color: 'var(--color-risk-text)' }}>
      <Trans>Billing is not available right now. Please try again later.</Trans>
    </p>
  ) : (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        tiers={config.tiers}
        promo={config.upgradePromo ?? null}
        initialPromoCode={carried?.promoCode || promoCode}
        attachedTrialCode={carried?.trialCode || ''}
        onUpgraded={onUpgraded}
        onClose={onClose}
      />
    </Elements>
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="glass-panel rounded-2xl p-6 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in"
        style={{ border: '1px solid var(--color-max-border)', boxShadow: '0 8px 32px 0 var(--color-max-glow)', backgroundColor: 'var(--color-bg)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-xl font-semibold flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
            <Trans>Upgrade to PrivacyPal Max</Trans>
          </h2>
          <button onClick={onClose} aria-label="Close" className="text-xl leading-none px-1" style={{ color: 'var(--color-text-muted)' }}>
            ×
          </button>
        </div>
        <p className="text-sm mb-5" style={{ color: 'var(--color-text-secondary)' }}>
          <Trans>Protect every AI surface: Cowork, Claude Code, Codex, Copilot, agents and the APIs, plus Connections, Private MCP, API access and full usage history.</Trans>
        </p>

        {hasLiveSubscription ? (
          <TierSwitchForm onUpgraded={onUpgraded} onClose={onClose} />
        ) : (
          <>
            {/* Two ways in, chosen before anything is typed. */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              {(['card', 'trial'] as MaxPath[]).map((key) => {
                const selected = path === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPath(key)}
                    className="p-3 rounded-lg text-left transition-all"
                    style={{
                      border: selected ? '2px solid var(--color-max-accent)' : '1px solid var(--color-border)',
                      backgroundColor: selected ? 'var(--color-max-bg)' : 'var(--color-surface)',
                    }}
                  >
                    <div className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
                      {key === 'card' ? <Trans>Start with a card</Trans> : <Trans>Company-wide trial</Trans>}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {key === 'card' ? <Trans>Live today, cancel anytime</Trans> : <Trans>Trial code or a 15 minute call</Trans>}
                    </div>
                  </button>
                );
              })}
            </div>

            {path === 'card' ? cardPanel : (
              <TrialCodeForm
                initialTrialCode={trialCode}
                source={source}
                referralCode={referralCode}
                onUseCard={(carry) => { setCarried(carry); setPath('card'); }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
